import React, { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import MarkerClusterer from 'marker-clusterer-plus';
import {
  ACTIVITY_BY_KEY, COL, flagOf, inIndia,
} from './mapMeta';

// The Google map with one pin per loan: fill = activity, ring = what needs
// action. Pins are clustered; clicking a pin selects the loan and opens its
// details in a pop-up (info window) on the pin: `renderPopup(relayout)`
// renders the React content into it. Uses the Maps JavaScript API already
// loaded by public/index.html (one map load).

const svgCircle = (size, fill) => `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(
  `<svg xmlns="http://www.w3.org/2000/svg" width="${size}" height="${size}">`
  + `<circle cx="${size / 2}" cy="${size / 2}" r="${size / 2 - 2}" fill="${fill}" fill-opacity="0.92" `
  + 'stroke="#fff" stroke-width="2"/></svg>',
)}`;

const CLUSTER_STYLES = [
  {
    url: svgCircle(34, '#3D4E8A'), width: 34, height: 34, textColor: '#fff', textSize: 11,
  },
  {
    url: svgCircle(42, '#2E3B6E'), width: 42, height: 42, textColor: '#fff', textSize: 12,
  },
  {
    url: svgCircle(52, '#1B2547'), width: 52, height: 52, textColor: '#fff', textSize: 12,
  },
];

const clusterCalculator = (markers) => {
  const n = markers.length;
  let index = 3;
  if (n < 50) index = 1;
  else if (n < 500) index = 2;
  return { text: n.toLocaleString('en-IN'), index, title: `${n.toLocaleString('en-IN')} loans — click to zoom in` };
};

const pinIcon = (row, selected) => {
  const activity = ACTIVITY_BY_KEY[row[COL.activity]] || ACTIVITY_BY_KEY.notStarted;
  const flag = flagOf(row);
  let stroke = activity.stroke || '#FFFFFF';
  if (flag) stroke = flag.color;
  if (selected) stroke = '#1F2733';
  return {
    path: window.google.maps.SymbolPath.CIRCLE,
    fillColor: activity.color,
    fillOpacity: 1,
    strokeColor: stroke,
    strokeWeight: selected || flag ? 3 : 1.5,
    scale: selected ? 11 : 7,
  };
};

export default function LoanMapCanvas({
  points, dataKey, selected, focusKey, onSelect, onBoundsChange, renderPopup, onClosePopup,
}) {
  const el = useRef(null);
  const map = useRef(null);
  const clusterer = useRef(null);
  const markers = useRef(new Map()); // loanId -> google.maps.Marker
  const highlight = useRef(null);
  const info = useRef(null);
  const popupEl = useRef(document.createElement('div'));
  const handlers = useRef({ onSelect, onBoundsChange, onClosePopup });
  handlers.current = { onSelect, onBoundsChange, onClosePopup };
  const lastFit = useRef(null);

  // The map, once.
  useEffect(() => {
    const { google } = window;
    if (!google || !el.current) return undefined;
    map.current = new google.maps.Map(el.current, {
      center: { lat: 22.5, lng: 79 },
      zoom: 5,
      mapTypeControl: false,
      streetViewControl: false,
      clickableIcons: false,
      fullscreenControl: true,
    });
    clusterer.current = new MarkerClusterer(map.current, [], {
      styles: CLUSTER_STYLES,
      calculator: clusterCalculator,
      gridSize: 56,
      maxZoom: 14,
      averageCenter: true,
    });
    info.current = new google.maps.InfoWindow({ content: popupEl.current, maxWidth: 340 });
    const closed = info.current.addListener('closeclick', () => handlers.current.onClosePopup());
    const idle = map.current.addListener('idle', () => {
      const b = map.current.getBounds();
      if (!b) return;
      const ne = b.getNorthEast();
      const sw = b.getSouthWest();
      handlers.current.onBoundsChange({
        north: ne.lat(), east: ne.lng(), south: sw.lat(), west: sw.lng(),
      });
    });
    return () => {
      idle.remove();
      closed.remove();
      info.current.close();
      clusterer.current.clearMarkers();
      markers.current.forEach((m) => m.setMap(null));
    };
  }, []);

  // New data (filters changed): drop the old pins.
  useEffect(() => {
    markers.current.forEach((m) => m.setMap(null));
    markers.current = new Map();
  }, [dataKey]);

  // Pins for the rows currently shown.
  useEffect(() => {
    const { google } = window;
    if (!google || !clusterer.current) return;
    const list = points.map((row) => {
      let m = markers.current.get(row[COL.loanId]);
      if (!m) {
        m = new google.maps.Marker({
          position: { lat: row[COL.lat], lng: row[COL.lng] },
          icon: pinIcon(row, false),
          title: `${row[COL.loanNumber] || ''} · ${row[COL.custName] || ''}`,
          optimized: true,
        });
        m.addListener('click', () => handlers.current.onSelect(row, false));
        markers.current.set(row[COL.loanId], m);
      }
      return m;
    });
    clusterer.current.clearMarkers();
    clusterer.current.addMarkers(list);

    // Frame the pins when the data changes (not when legend toggles).
    if (lastFit.current !== dataKey && points.length) {
      lastFit.current = dataKey;
      const bounds = new google.maps.LatLngBounds();
      const india = points.filter((r) => inIndia(r[COL.lat], r[COL.lng]));
      (india.length >= points.length / 2 ? india : points)
        .forEach((r) => bounds.extend({ lat: r[COL.lat], lng: r[COL.lng] }));
      map.current.fitBounds(bounds, 40);
      if (points.length === 1) map.current.setZoom(15);
    }
  }, [points, dataKey]);

  // Selected loan: a bigger pin on top, outside the clusters.
  useEffect(() => {
    const { google } = window;
    if (!google || !map.current) return;
    if (highlight.current) highlight.current.setMap(null);
    highlight.current = null;
    if (!selected) {
      info.current.close();
      return;
    }
    highlight.current = new google.maps.Marker({
      map: map.current,
      position: { lat: selected[COL.lat], lng: selected[COL.lng] },
      icon: pinIcon(selected, true),
      zIndex: 9999,
      title: `${selected[COL.loanNumber] || ''} · ${selected[COL.custName] || ''}`,
    });
    const open = () => info.current.open({
      anchor: highlight.current, map: map.current, shouldFocus: false,
    });
    highlight.current.addListener('click', open);
    open();
  }, [selected]);

  // Re-measure the pop-up after its content changes size (details loaded).
  const relayout = () => {
    if (info.current && selected) info.current.setContent(popupEl.current);
  };

  // Bring the selected loan into view when picked from the list / search.
  // Pan only (the selected pin is drawn above the clusters), so the zoom and
  // the "loans in view" list stay as they were.
  useEffect(() => {
    if (!focusKey || !selected || !map.current) return;
    map.current.panTo({ lat: selected[COL.lat], lng: selected[COL.lng] });
  }, [focusKey]);

  return (
    <>
      <div ref={el} className='lm-map-canvas' />
      {selected && renderPopup ? createPortal(renderPopup(relayout), popupEl.current) : null}
    </>
  );
}
